import {
  IInstitution,
  IError,
  LoadingOverlay,
  useToast,
} from '@comptia-sso/core';
import { useFormik } from 'formik';
import { ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

// Components.
import { Card, EditableContent, EditableDetail } from 'components';

// Hooks.
import { useUpdateInstitution } from 'hooks';

// Styles.
import styles from './InstitutionDetails.module.scss';

// Types.
import { IAccount } from 'types';

interface IInstitutionDetailsProps {
  institution: IInstitution;
  account?: IAccount;
  mode: string;
}

const validationSchema = yup.object().shape({
  legalAgreementId: yup.string().required('Legal Agreement Id is required'),
  name: yup.string().required('Name is required'),
});

export const InstitutionDetails = (
  props: IInstitutionDetailsProps,
): ReactElement => {
  const { institution, account, mode } = props;
  const [isEditing, setIsEditing] = useState<boolean>(mode !== 'edit');
  const [popToast] = useToast();
  const [updateInstitution] = useUpdateInstitution(institution?.id);
  const navigate = useNavigate();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      isActive: institution?.isActive ?? true,
      legalAgreementId: institution?.legalAgreementId || '',
      name: institution?.name || '',
    },
    onSubmit: async (values) => {
      if (account?.permissions?.editInstructorApprovers) {
        try {
          await updateInstitution(values);
          setIsEditing(false);
          popToast('Institution successfully saved.');
          if (mode !== 'edit') {
            navigate('/consent-approvers');
          }
        } catch (error) {
          popToast(
            (error as IError)?.message ||
              'An unexpected error occurred. Please try again later.',
          );
        }
      } else {
        popToast('You do not have permission to perform this action.');
      }
    },
    validateOnChange: false,
    validationSchema,
  });

  return (
    <div className={styles.content}>
      <EditableContent
        canEdit={account?.permissions?.editInstructorApprovers ?? false}
        isEditing={isEditing}
        onCancel={() => {
          formik.resetForm();
          if (mode !== 'edit') {
            navigate('/consent-approvers');
          }
          setIsEditing(false);
        }}
        onEdit={() => setIsEditing(true)}
        formik={formik}
        title="Institution"
      >
        <Card className={styles.grid}>
          <EditableDetail
            containerClassName={styles.fullWidth}
            label="Name"
            name="name"
            required
          />
          <EditableDetail
            containerClassName={styles.fullWidth}
            label="Legal Agreement Id"
            name="legalAgreementId"
            required
          />
          <EditableDetail
            label="Active"
            name="isActive"
            renderValue={(value) => (value ? 'Yes' : 'No')}
            select
          >
            <option value="true">Yes</option>
            <option value="false">No</option>
          </EditableDetail>
        </Card>
      </EditableContent>
      <LoadingOverlay isOpen={formik.isSubmitting} text="Saving Institution" />
    </div>
  );
};
